/**
* Licensed Materials - Property of IBM
* IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2018
* US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/

define([
	'../../lib/@waca/dashboard-common/dist/core/Model',
	'underscore'
],
function(Model, _) {
	'use strict';


	var HierarchicalFilterModel = Model.extend({
		whitelistAttrs: ['name', 'selectedMembers', 'inverted'],

		init: function(options) {
			HierarchicalFilterModel.inherited('init', this, arguments);
			// the API requires a single invisible root to contain the other roots identified as a 'null'
			// here we keep the query result model of:
			// u for the use value/ ID
			// d for the display value / caption
			// p for the parent.
			// we also add some house keeping values, like children and selected
			//
			// once the tree is constructed, from each item the siblings, parent, and children are accessible
			//
			this.items = (options && options.items) || {
				'null': {
					u: null,
					d: '',
					p: null,
					selected: false,
					childrenLoaded: true, // roots are always considered loaded. We anticipate reset will be called to set them. If not then the tree is empty.
					children: []
				}
			};
			this.searchResults = [];
			this.selectedMembers = {};
			this._rowLimit = options.rowLimit;
		},
		reset: function(options = {}){
			const roots = options.roots || [];
			const ascendants = options.ascendants || [];
			const initialSelection = options.initialSelection || [];
			const inverted = _.isBoolean(options.inverted) ? options.inverted : false;
			const silent = _.isBoolean(options.silent) ? options.silent : false;
			this.searchResults = [];
			this.items = { 'null': this.items[null] };
			this.items[null].children = [];
			this.inverted = inverted || false;
			this.addItems([...roots, ...ascendants], { silent });
			this.setSelectedMembers(initialSelection,  { silent });
			if (!silent) {
				this.trigger('reset');
			}
		},
		setName: function (name){
			this.set({ name: name });
		},
		getName: function (){
			return this.get('name') || '';
		},
		addItems: function (items , options = {}){
			const silent = _.isBoolean(options.silent) ? options.silent : false;

			_.each(items, function(item){
				this._addItem(item);
			}.bind(this));

			if(!silent){
				this.trigger('change:content');
			}
		},

		/* this does not trigger event, use addItems instead*/
		_addItem: function(item){
			var parentUseValue = null;
			if( item.p ){
				if( item.p.u ) {
					parentUseValue = item.p.u;
				}else{
					parentUseValue = item.p;
				}
			}
			var parent = this.items[parentUseValue];
			if( !parent){
				throw new Error('missing parent');
			}
			// loading an existing filter causes a sparse tree to be created with the items from the selection.
			// in that case we already some items and we keep them as-is since they may have a selected state and children already.
			// this implies that we never refresh the data.
			if(!this.items[item.u]){
				var newItem = {
					u: item.u,
					d: item.d,
					p: parentUseValue,
					selected: false,
					childrenLoaded: false,
					children: []
				};
				this.items[newItem.u] = newItem;
				parent.children.push(newItem);
			}else if( this.items[item.u].p === undefined && item.p !== undefined){
				this.items[item.u].p = item.p;
				parent.children.push(this.items[item.u]);
			}
		},
		setSearchResults: function(results){
			this.searchResults = _.map(results, function(result){
				var item = this.items[result.u];
				if( !item ){
					// when we don't already have an item then we probably don't have it's parent info
					// so we don't know where in the tree it goes.
					//
					// We add it to our main item tree as a top level root (above the displayed roots). This allows our selection
					// code to find them and addItem will detect this condition if we add the parents later.
					// This occurs when switching from list/search to tree.
					// The same item will be preserved but moved to a real parent if it was selected.
					result.p = undefined;
					this._addItem(result);
					item = this.items[result.u];
				}
				return item;
			}.bind(this));
			this.trigger('change:content');
		},

		loadChildren: function( parentUseValue, children, offset = 0){
			this.addItems(children);
			var parent = this.items[parentUseValue];

			//TODO childrenLoaded property can be deleted. Tracking if children is loaded
			//can be done via the value of offest
			parent.childrenLoaded = true;

			parent.offset = offset;
			this.trigger('load:children');
		},
		childrenLoaded: function(parentUseValue, offset = 0){
			var parent = this.items[parentUseValue];
			return parent && parent.childrenLoaded && parent.offset >= offset;
		},

		listGetValue: function(index){
			var item = this.searchResults[index] || {};
			return {
				id: item.u,
				caption: item.d,
				selected: item.selected,
				descendantsSelected: false,
				hasChildren: false,
				subcaption: ''
			};
		},
		listGetNumberOfChildren: function(){
			return this.searchResults ? this.searchResults.length : null;
		},

		_getItem: function(parentUseValue, rowIdx){
			var parent = this.items[parentUseValue];
			// if row is specified we are looking for a child item
			return  rowIdx !== undefined ? parent.children[rowIdx] : parent;
		},

		getValue: function (parentUseValue, rowIdx) {

			const item = this._getItem(parentUseValue, rowIdx);

			// the only case where hasChildren is false is when we know for sure we have no children.
			// i.e. loaded and zero children
			var hasChildren = !(item.childrenLoaded && item.children.length === 0);
			return {
				id: item.u,
				caption: item.d,
				selected: this.inverted ? !item.selected : item.selected,
				descendantsSelected: this._descendantsSelected(item.u),
				hasChildren: hasChildren
			};
		},

		/**
		 * The children limit is the number of children shown in the UI
		 * before showing the UI item that prompts the user to 'load more'
		 * children.
		 * By design, the children limit is 1 less than the row limit - number
		 * of children fetched from the database-
		 * 
		 * So when 51 children are fetched from the database from offset 0, getChildrenLimit
		 * returns 50, so that an item with the load more prompt is put in the 51st position
		 */
		getChildrenLimit:function(itemId){
			const item =  this._getItem(itemId) || { offset: 0 };
			const offset = item.offset ? item.offset : 0;
			return offset + this._rowLimit - 1;
		},

		getNumberOfChildren: function(id){
			var numberOfChildren = null;

			var item = this.items[id];
			if( item && item.childrenLoaded ){
				numberOfChildren = item.children.length;
			}
			return numberOfChildren;
		},
		_descendantsSelected: function(parentUseValue){
			function getFirstSelectedDecendant(item){
				return item.selected ? item : _.find(item.children, getFirstSelectedDecendant);
			}
			var parent = this.items[parentUseValue];
			return parent && Boolean(_.find(parent.children, getFirstSelectedDecendant));
		},

		/**
		 * inverses the selected state of the item identified by itemUseValue
		 */
		toggleSelectState: function (itemUseValue) {
			var item = this.items[itemUseValue];
			if( item ){
				item.selected = !item.selected;

				if (item.selected) {
					this.selectedMembers[itemUseValue] = { u:item.u, d: item.d };
				} else {
					// item has to be selected to come here
					delete this.selectedMembers[itemUseValue];
				}
				// if needed we can trigger change:selectedMembers instead but getSelectedMembers() is expensive
				this.trigger('change:selectedMember', { useValue:itemUseValue, state: item.selected });
			}
		},

		/**
		 * @return {Array} array of useValue of selected members
		 */
		getSelectedMembers: function () {
			var selectedMembers = [];

			_.each(this.items, function(item){
				if (item.selected){
					selectedMembers.push({ u:item.u, d: item.d });
				}
			});
			return selectedMembers;
		},
		/**
		 * returns an array of selectedItems selected in search list.
		 *
		 *  @return {Array} array of selectedItems
		 */
		getSelectedItemsFromSearch: function(){
			let _selectedItemsFromSearch = [];
			_.each(this.searchResults, function(item){
				if( item.selected && !item.p){
					_selectedItemsFromSearch.push({ u:item.u, d: item.d });
				}
			}.bind(this));
			return _selectedItemsFromSearch;
		},
		/**
		 * selects the provided array of usevalues and de-select all others.
		 * @param  selectedUseValues array of usevalues to select
		 */
		setSelectedMembers: function (selectedUseValues, options = {}) {
			const silent = _.isBoolean(options.silent) ? options.silent : false;

			_.each(this.items, function(item){
				item.selected = false;
			});
			this.selectedMembers = {};
			_.each(selectedUseValues, function(itemUseValue){
				var item = this.items[itemUseValue];
				if( item ){
					item.selected = true;
					this.selectedMembers[itemUseValue] = { u:item.u, d: item.d };
				}
			}.bind(this));

			if(!silent){
				this.trigger('change:selectedMembers', { selectedMembers: this.getSelectedMembers() });
			}
		},

		/**
		 * de-select all items.
		 */
		clearValues: function(){
			this.setInvert(false);
			this.setSelectedMembers([]);
		},

		getInvert: function(){
			return this.inverted || false;
		},
		setInvert: function (trueOrFalse) {
			this.set({ inverted: trueOrFalse });
		}
	});

	return HierarchicalFilterModel;
});
